import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap/nav';
import { TranslatePipe } from '@ngx-translate/core';
import {
  PasSidebarHandleService,
  PasTabContentDirective,
  PasTabDirective,
  PasTabsetComponent,
  PasTabTitleDirective,
} from '@porsche-aftersales/frontend-toolbox';
import { Subscription } from 'rxjs';

import { hasAfterSalesSuppliersAndRatesRight } from '../../services/after-sales/after-sales-functions';
import { UserService } from '../../services/user-service/user.service';

import { AfterSalesDetailsComponent } from './after-sales-details/after-sales-details.component';
import { AfterSalesComponent } from './after-sales-overview/after-sales.component';
import { AfterSalesErrorCostRateComponent } from './error-cost-rate/after-sales-error-cost-rate.component';
import { SupplierExceptionsComponent } from './supplier-exceptions/supplier-exceptions.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'after-sales-tabset',
  styleUrls: ['./after-sales-tabset.component.scss'],
  templateUrl: './after-sales-tabset.component.html',
  imports: [
    PasTabsetComponent,
    PasTabDirective,
    TranslatePipe,
    AfterSalesComponent,
    AfterSalesDetailsComponent,
    AfterSalesErrorCostRateComponent,
    SupplierExceptionsComponent,
    PasTabTitleDirective,
    PasTabContentDirective,
  ],
})
export class AfterSalesTabsetComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdRef = inject(ChangeDetectorRef);

  @ViewChild(PasTabsetComponent)
  public tabset: PasTabsetComponent;

  @ViewChild(AfterSalesDetailsComponent)
  public afterSalesDetails: AfterSalesDetailsComponent;

  public static readonly AFTERSALES_OVERVIEW_TAB = 'overview';
  public static readonly AFTERSALES_DETAILS_TAB = 'details';
  public static readonly AFTERSALES_ERROR_COST_RATE_TAB = 'errorCostRates';
  public static readonly AFTERSALES_SUPPLIER_EXCEPTIONS_TAB = 'supplierExceptions';

  public readonly OVERVIEW_TAB: string = AfterSalesTabsetComponent.AFTERSALES_OVERVIEW_TAB;
  public readonly DETAILS_TAB: string = AfterSalesTabsetComponent.AFTERSALES_DETAILS_TAB;
  public readonly ERROR_COST_RATE_TAB: string = AfterSalesTabsetComponent.AFTERSALES_ERROR_COST_RATE_TAB;
  public readonly SUPPLIER_EXCEPTIONS_TAB: string = AfterSalesTabsetComponent.AFTERSALES_SUPPLIER_EXCEPTIONS_TAB;

  public detailsTabDisabled = true;
  public detailsTabHidden = true;
  private pathParamSubscription: Subscription;
  public routerParamName = 'tabId';

  public ngOnInit(): void {
    this.getPathParam();
  }

  /**
   * If path param 'id' is available in route, e.g. '/afterSales/tabs/details/:id'
   * Report details view (tab) must be enabled in order to be opened via url path
   * If details view is already open, fields must be reinitialized
   */
  private getPathParam(): void {
    this.pathParamSubscription = this.route.params.subscribe(params => {
      const reportId = params.id;
      if (params.tabId === this.DETAILS_TAB && reportId) {
        this.setTabVisibility(this.DETAILS_TAB);
        this.reinitializeDetailsView();
      }
    });
  }

  /**
   * prevent Angular error “Expression has changed after it was checked” in dev mode
   */
  public ngAfterViewInit(): void {
    this.cdRef.detectChanges();
  }

  public onChangingTab(event: NgbNavChangeEvent): void {
    this.setTabVisibility(event.nextId);
    if (event.activeId === this.DETAILS_TAB && event.nextId !== this.DETAILS_TAB) {
      // after opening details tab, reportId stays as path parameter, e.g. /afterSales/tabs/overview/:reportId
      event.preventDefault();
      this.router.navigate([`/afterSales/tabs/${event.nextId}`]);
    }
  }

  private setTabVisibility(tabId: string): void {
    const isDetailsTabDisabledAndHidden: boolean = tabId !== this.DETAILS_TAB;
    this.detailsTabDisabled = isDetailsTabDisabledAndHidden;
    this.detailsTabHidden = isDetailsTabDisabledAndHidden;
  }

  public onCancel(): void {
    this.navigateToOverview();
  }

  public ngOnDestroy(): void {
    this.sidebarHandleService.disableSidebarForView();
    this.pathParamSubscription.unsubscribe();
  }

  public userHasAnyRight(): boolean {
    return this.userService.getRights().length > 0;
  }

  public reinitializeDetailsView(): void {
    if (this.afterSalesDetails) {
      this.afterSalesDetails.initializeView();
      this.afterSalesDetails.refreshBookingsTable();
    }
  }

  private navigateToOverview(): void {
    this.router.navigate([`/afterSales/tabs/${this.OVERVIEW_TAB}`]);
  }

  public navigateToDetails(reportId: string): void {
    this.router.navigate([`/afterSales/tabs/${this.DETAILS_TAB}/${reportId}`]);
  }

  public navigateToErrorCostRate(): void {
    this.router.navigate([`/afterSales/tabs/${this.ERROR_COST_RATE_TAB}`]);
  }

  public errorCostRatesTabVisible(): boolean {
    return hasAfterSalesSuppliersAndRatesRight(this.userService.getRights());
  }
}
